<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { CsEmptyState } from '../empty-state';
import { CsFormError, CsFormHelpText, CsFormLabel } from '../form';
import { CsIcon } from '../icon';
import { CsInput } from '../input';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../popover';
import { CsText } from '../text';
import emojisGroup from '../../../../../shared/components/emoji/emojisGroup.json';

/**
 * @typedef {import('dashboard/components/ui/commslayer/icon/icon-types').IconName} IconName
 */

/**
 * @typedef {object} EmojiPickerProps
 * @property {string} [label] - Label for the emoji picker
 * @property {string} [error] - Error message for the emoji picker
 * @property {string} [helpText] - Help text for the emoji picker
 * @property {boolean} [isShowHistory=false] - Whether to show the emoji history section
 * @property {number} [maxHistory=12] - Maximum number of emojis to store in history
 * @property {IconName} [placeholderIcon='smiley-happy'] - Placeholder icon for the emoji picker
 */

/**
 * @type {EmojiPickerProps}
 */
const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: 'Emoji Picker',
  },
  error: {
    type: String,
    default: '',
  },
  helpText: {
    type: String,
    default: '',
  },
  isShowHistory: {
    type: Boolean,
    default: false,
  },
  maxHistory: {
    type: Number,
    default: 12,
  },
  placeholderIcon: {
    type: String,
    default: 'smiley-happy',
  },
  zIndex: {
    type: Number,
    default: 2000,
  },
});

const emit = defineEmits(['input', 'change']);

const search = ref('');
const debouncedSearch = ref('');
const emojis = ref(emojisGroup);
const history = ref([]);
const searchInputRef = ref(null);

const displayValue = computed(() => {
  const emoji = extractEmoji(props.value);
  return emoji || '';
});

const filteredEmojis = computed(() => {
  if (!debouncedSearch.value) {
    return emojis.value;
  }

  const searchTerm = debouncedSearch.value.toLowerCase();
  return emojis.value
    .map(category => ({
      ...category,
      emojis: category.emojis.filter(emoji =>
        emoji.name.toLowerCase().includes(searchTerm)
      ),
    }))
    .filter(category => category.emojis.length > 0);
});

function getCategoryEmoji(emojis) {
  return emojis[0].emoji;
}

function onSelectEmoji(payload) {
  emit('input', payload.emoji, payload);
  emit('change', payload.emoji, payload);

  addToHistory(payload);
}

function onClickCategory(categoryId) {
  const element = document.getElementById(categoryId);
  element.scrollIntoView({ behavior: 'smooth' });
}

function addToHistory(emoji) {
  // Add new emoji to the beginning of the array
  history.value.unshift(emoji);

  // Keep only the most recent emojis based on maxHistory prop
  history.value = history.value.slice(0, props.maxHistory);
  window.localStorage.setItem('emojis-history', JSON.stringify(history.value));
}

function refreshHistory() {
  const getHistory = window.localStorage.getItem('emojis-history');
  history.value = getHistory ? JSON.parse(getHistory) || [] : [];
}

function handleOpenPopover() {
  refreshHistory();
  searchInputRef.value?.focus();
}

function handleClosePopover() {
  search.value = '';
  debouncedSearch.value = '';
}

function extractEmoji(value) {
  return value.split(' ')[0];
}

function onSearchInputBlur() {
  setTimeout(() => {
    searchInputRef.value?.focus();
  }, 100);
}

function onClearEmoji() {
  emit('input', '');
  emit('change', '');
}

// Add debounced watcher for search
watch(
  () => search.value,
  newValue => {
    debouncedSearch.value = newValue;
  },
  { debounce: 300 }
);

onMounted(async () => {
  // Refresh history
  if (props.isShowHistory) {
    refreshHistory();
  }
});
</script>

<template>
  <CsPopover
    v-slot="{ isOpen, close }"
    is-teleport
    class="inline-flex flex-col"
    @open="handleOpenPopover"
    @close="handleClosePopover"
  >
    <CsPopoverTrigger class="inline-flex">
      <div class="flex flex-col">
        <CsFormLabel v-if="props.label" class="mb-1">
          {{ props.label }}
        </CsFormLabel>

        <div
          class="h-[34px] min-w-[34px] pl-2 pr-1 cursor-pointer flex items-center justify-center gap-1 bg-brand-gray-1 border rounded-lg dark:bg-brand-gray-16"
          :class="[
            isOpen
              ? 'border-brand-gray-16 bg-brand-gray-3 dark:bg-brand-gray-16'
              : 'border-brand-gray-12 dark:border-brand-gray-13 hover:bg-brand-gray-3 hover:dark:bg-brand-gray-16',
            { 'opacity-50 cursor-not-allowed': props.isDisabled },
          ]"
        >
          <CsIcon
            v-if="!displayValue"
            :name="props.placeholderIcon"
            class="h-6 w-6"
          />
          <span v-else class="text-xl">
            {{ displayValue }}
          </span>

          <CsIcon name="select" variant-color="secondary" class="h-5 w-5" />
        </div>
      </div>
    </CsPopoverTrigger>

    <CsPopoverContent :z-index="zIndex">
      <CsPopoverCard>
        <CsPopoverCardContent class="min-w-[400px]">
          <div class="p-2">
            <CsInput
              ref="searchInputRef"
              v-model="debouncedSearch"
              placeholder="Search"
              @blur="onSearchInputBlur"
            />
          </div>

          <CsEmptyState
            v-if="filteredEmojis.length === 0"
            icon-type="no-result-found"
            heading="No results found"
            content="Your search didn't return any results."
            class="py-8"
          />

          <div
            v-else
            class="flex flex-col px-2 py-1 gap-4 max-h-[250px] overflow-y-auto"
          >
            <div
              v-if="
                history.length > 0 && props.isShowHistory && !debouncedSearch
              "
            >
              <CsText id="history" weight="medium" class="mb-1">
                History
              </CsText>

              <div class="grid grid-cols-8 gap-1">
                <button
                  v-for="(emoji, index) in history"
                  :key="index"
                  v-cs-tooltip="emoji.name"
                  class="text-3xl cursor-pointer h-10 w-10 flex items-center justify-center p-0.5 rounded-md hover:bg-brand-gray-6 dark:hover:bg-brand-gray-14"
                  @click="
                    onSelectEmoji(emoji);
                    close();
                  "
                  v-html="emoji.emoji"
                />
              </div>
            </div>

            <div v-for="category in filteredEmojis" :key="category.name">
              <CsText :id="`${category.slug}`" weight="medium" class="mb-1">
                {{ category.name }}
              </CsText>

              <div class="grid grid-cols-8 gap-1">
                <button
                  v-for="children in category.emojis"
                  :key="children.name"
                  v-cs-tooltip="children.name"
                  class="text-3xl cursor-pointer h-10 w-10 flex items-center justify-center p-0.5 rounded-md hover:bg-brand-gray-6 dark:hover:bg-brand-gray-14"
                  @click="
                    onSelectEmoji(children);
                    close();
                  "
                  v-html="children.emoji"
                />
              </div>
            </div>
          </div>

          <footer
            class="flex items-center justify-between gap-8 px-2 py-1 border-t border-brand-border bg-brand-background-secondary rounded-b-xl"
          >
            <div
              v-if="filteredEmojis.length > 0"
              class="flex flex-wrap items-center gap-1.5"
            >
              <button
                v-if="history.length > 0 && props.isShowHistory"
                v-cs-tooltip="'History'"
                class="text-xl cursor-pointer w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gray-6 dark:hover:bg-brand-gray-14"
                @click="onClickCategory('history')"
              >
                <CsIcon name="clock" class="h-6 w-6" />
              </button>
              <button
                v-for="category in emojis"
                :key="category.name"
                v-cs-tooltip="category.name"
                class="text-xl cursor-pointer h-8 w-8 flex items-center justify-center rounded-md hover:bg-brand-gray-6 dark:hover:bg-brand-gray-14"
                @click="onClickCategory(category.slug)"
              >
                {{ getCategoryEmoji(category.emojis) }}
              </button>
            </div>

            <button
              v-if="displayValue"
              class="h-6 px-2 flex items-center justify-center gap-1 cursor-pointer hover:bg-brand-gray-6 dark:hover:bg-brand-gray-14 rounded-md"
              @click="
                () => {
                  onClearEmoji();
                  close();
                }
              "
            >
              <CsText variant-color="critical"> Clear </CsText>
            </button>
          </footer>
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>

    <div v-if="props.error || props.helpText" class="flex flex-col mt-1 gap-1">
      <CsFormError v-if="props.error">
        {{ props.error }}
      </CsFormError>

      <CsFormHelpText v-if="props.helpText">
        {{ props.helpText }}
      </CsFormHelpText>
    </div>
  </CsPopover>
</template>
